/**
 * ScoreShimmer - Shimmer overlay for estimated scores
 *
 * Wraps score display with a shimmer/pulse animation during the
 * estimation phase. Falls back to a static gray overlay when
 * prefers-reduced-motion is enabled.
 *
 * @module components/modern-results/ScoreShimmer
 */

import { View, StyleSheet } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  cancelAnimation,
  Easing,
} from 'react-native-reanimated';
import { useEffect } from 'react';
import { TierBody } from '@dramadetector/ui';
import { useTranslation } from '@dramadetector/shared';

interface ScoreShimmerProps {
  /** Whether shimmer is active */
  isLoading: boolean;
  /** Whether user prefers reduced motion */
  isReducedMotion?: boolean;
  /** Child content to overlay */
  children: React.ReactNode;
}

/**
 * Wraps children with a pulsing shimmer effect while loading.
 * Shows an "Estimated" label below the content.
 */
export function ScoreShimmer({
  isLoading,
  isReducedMotion,
  children,
}: ScoreShimmerProps) {
  const { t } = useTranslation('features');
  const opacity = useSharedValue(1);

  useEffect(() => {
    if (isLoading && !isReducedMotion) {
      opacity.value = withRepeat(
        withTiming(0.5, { duration: 1000, easing: Easing.inOut(Easing.ease) }),
        -1,
        true
      );
    } else {
      cancelAnimation(opacity);
      opacity.value = 1;
    }

    return () => cancelAnimation(opacity);
  }, [isLoading, isReducedMotion]);

  const shimmerStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  if (!isLoading) {
    return <>{children}</>;
  }

  return (
    <View testID="score-shimmer">
      <Animated.View style={shimmerStyle}>{children}</Animated.View>
      <TierBody size="xs" color="text-white" style={shimmerStyles.estimatedLabel}>
        {t('score_card.estimated_label', { defaultValue: 'Estimated' })}
      </TierBody>
    </View>
  );
}

const shimmerStyles = StyleSheet.create({
  estimatedLabel: {
    opacity: 0.7,
    marginTop: 4,
  },
});
